import { Box, Tooltip } from '@mui/material';
import { LabeledValue, WidgetTable_Cell, WidgetTable_Row } from 'emerald-ui';
import { staticUrl } from 'emerald-ui/dist/globals/urls';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import {
    CargoToken_Styled,
    CargoWidget_Button,
    CargoWidget_ContainerNumber,
    CargoWidget_ContainerType,
    CargoWidget_DrillIn,
    CargoWidget_Label,
    CargoWidget_Values,
    CopyLinkIcon
} from './CargoWidgetRow.style';
import { copyStringToClipboard } from 'emerald-ui/dist/utilities/copyStringToClipboard';

/**
 * @typedef CargoWidgetRowDataItem
 * @property {string | number} [id]
 * @property {string} type
 * @property {string} containerNumber
 * @property {string} trackingNumber
 * @property {LabelValue[]} labelValueDetails
 */

/**
 * @typedef CargoWidgetRowProps
 * @property {CargoWidgetRowDataItem} dataRow An object containing the data to be rendered.
 */

/** @type {(props: CargoWidgetRowProps & React.HTMLAttributes<HTMLDivElement>) => JSX.Element} */
export const CargoWidgetRow = (props) => {
    const [copiedLink, setCopiedLink] = useState(false);
    const [showFocusText, setShowFocusText] = useState(false);

    /** @type {React.MutableRefObject<number | undefined>} */
    const timeoutRef = useRef();

    useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, []);

    useEffect(() => {
        if (copiedLink) {
            if (timeoutRef.current !== undefined) {
                clearTimeout(timeoutRef.current);
            }
            /**
             * Typescript thinks this code is going to run server-side and return a NodeJS.Timeout
             * instead of a number.
             */
            // @ts-ignore
            timeoutRef.current = setTimeout(() => { setShowFocusText(false); setCopiedLink(false); }, 3000);
        }
    }, [copiedLink]);

    /** @type {React.MouseEventHandler<HTMLButtonElement>} */
    const handleCopyClick = async (e) => {
        const cargoPageUrl = window.location.origin;
        const trackingUrl = `${cargoPageUrl}/track?id=${e.currentTarget.dataset.trackingNumber}`;
        await copyStringToClipboard(trackingUrl);
        setCopiedLink(true);
    };

    const {
        dataRow,
        ...otherProps
    } = props;

    return (
        <WidgetTable_Row
            {...otherProps}
        >
            <WidgetTable_Cell>
                <CargoToken_Styled />
                <LabeledValue as='div'>
                    <CargoWidget_ContainerType aria-label={`Container Type ${dataRow.type}`}>
                        <div aria-hidden>
                            {dataRow.type}
                        </div>
                    </CargoWidget_ContainerType>
                    <CargoWidget_ContainerNumber aria-label={`Container Number ${dataRow.containerNumber}`}>
                        <div aria-hidden>
                            {dataRow.containerNumber}
                        </div>
                    </CargoWidget_ContainerNumber>
                </LabeledValue>
            </WidgetTable_Cell>
            {dataRow.labelValueDetails.map(({ label, value }) => {
                return (
                    <Box
                        key={label}
                        sx={{ display: { xs: 'none', sm:'block' } }}
                    >
                        <WidgetTable_Cell>
                            <LabeledValue as='div'>
                                <CargoWidget_Label as='div'>
                                    {label}
                                </CargoWidget_Label>
                                <CargoWidget_Values as='div'>
                                    {value || '---'}
                                </CargoWidget_Values>
                            </LabeledValue>
                        </WidgetTable_Cell>
                    </Box>
                );
            })}
            <WidgetTable_Cell alignRight>
                {dataRow.trackingNumber ? (
                    <>
                        <Link href={`/track?id=${dataRow.trackingNumber}`}>
                            <Tooltip
                                placement='top'
                                title='Open Tracking Page'
                            >
                                <CargoWidget_Button aria-label='Open Tracking Page'>
                                    <CargoWidget_DrillIn />
                                </CargoWidget_Button>
                            </Tooltip>
                        </Link>
                        <Tooltip
                            open={copiedLink || showFocusText}
                            placement='top'
                            title={copiedLink ? 'Tracking Link Copied' : 'Copy Tracking Link'}
                        >
                            <CargoWidget_Button
                                aria-label='Copy Tracking Link'
                                data-tracking-number={dataRow.trackingNumber}
                                onBlur={() => setShowFocusText(false)}
                                onClick={handleCopyClick}
                                onFocus={() => setShowFocusText(true)}
                                onMouseLeave={() => setShowFocusText(false)}
                                onMouseOver={() => setShowFocusText(true)}
                            >
                                <CopyLinkIcon
                                    url={staticUrl('images/copy-link.svg')}
                                />
                            </CargoWidget_Button>
                        </Tooltip>
                    </>
                )
                    : null}
            </WidgetTable_Cell>
        </WidgetTable_Row>
    );
};
